<!--
 * @Descripttion: 工业场景价格配置
 * @Date: 2026-07-02 10:00:00
-->
<template>
  <div class="scene-price-config box-flex-column" v-xrk-loading="loading">
    <div class="page-header">
      <div class="page-header__left">
        <div class="page-header__title-row">
          <BaseButton link type="primary2" @click="onBack">返回列表</BaseButton>
          <span class="divider">/</span>
          <h1 class="page-title">{{ pageTitle }}</h1>
        </div>
        <div class="industry-name">
          工业名称：
          <span class="industry-name__value">{{ industryName }}</span>
        </div>
        <div class="page-tip">
          工业价格默认等于平台价格区间；可超出平台区间，保存时需二次确认。数字区间须由低到高。服务商专属价优先于工业通用价。
        </div>
      </div>
      <div class="page-header__actions">
        <BaseButton @click="onBack">取消</BaseButton>
        <BaseButton type="primary" :loading="saving" @click="onSave">
          保存
        </BaseButton>
      </div>
    </div>

    <div class="toolbar">
      <BaseButton @click="onOpenPicker">选择开通场景</BaseButton>
      <BaseInput
        v-model="keyword"
        clearable
        placeholder="搜索场景名称"
        style="width: 260px"
      />
      <span class="toolbar-summary">{{ summaryText }}</span>
    </div>

    <div class="table-wrap">
      <div class="table-wrap__body">
        <BaseTable :data="pagedScenes" :columns="columns" />
      </div>
      <BasePagination
        class="config-pagination"
        :current-page="currentPage"
        :page-size="pageSize"
        :page-sizes="[10, 15, 30, 50]"
        :layout="['total', 'sizes', 'prev', 'pager', 'next', 'jumper']"
        :total="filteredScenes.length"
        @current-change="onPageChange"
        @size-change="onPageChange"
      />
    </div>
  </div>
</template>

<script lang="tsx" setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  BaseButton,
  BaseInput,
  BasePagination,
  BaseTable,
  BaseTableProps,
  useCreateModal,
  useTableColumn,
  XrkMessage,
  XrkMessageBox
} from 'xrk-components';
import ScenePriceScenePickerModal from '../components/scene-price-scene-picker-modal.vue';
import { getConfigDetail, saveScenes } from '../data-source/apis';
import type {
  ScenePriceConfigDetail,
  ScenePriceSceneItem
} from '../data-source/types';
import {
  formatPriceInputText,
  formatPriceRange,
  getEffectiveIndustryRange,
  getPlatformRangeWarning,
  hasRangeError,
  isOutOfPlatformRange,
  validatePriceRange
} from '../utils/price-range';
import { MAX_PRICE, MIN_PRICE, PRICE_DECIMALS } from '../constants';

type EditableScene = ScenePriceSceneItem & {
  minText: string;
  maxText: string;
  minError: string;
  maxError: string;
  minWarn: string;
  maxWarn: string;
};

const route = useRoute();
const router = useRouter();

const loading = ref(false);
const saving = ref(false);
const keyword = ref('');
const currentPage = ref(1);
const pageSize = ref(15);
const industryId = ref<string>((route.query.industryId as string) || '');
const industryName = ref<string>((route.query.industry as string) || '');
const isCreateMode = computed(() => route.query.mode === 'create');
const pageTitle = computed(() =>
  isCreateMode.value ? '新增工业价格配置' : '工业场景价格配置'
);

const scenes = ref<EditableScene[]>([]);

const filteredScenes = computed(() => {
  const q = keyword.value.trim();
  const enabledScenes = scenes.value.filter(item => item.enabled);
  if (!q) {
    return enabledScenes;
  }
  return enabledScenes.filter(item => item.scenePath.includes(q));
});

/** 当前页数据 */
const pagedScenes = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value;
  return filteredScenes.value.slice(start, start + pageSize.value);
});

/** 分页切换（BasePagination 通过事件回传页码） */
const onPageChange = (payload: { currentPage?: number; pageSize?: number }) => {
  if (payload.pageSize != null) {
    pageSize.value = payload.pageSize;
  }
  if (payload.currentPage != null) {
    currentPage.value = payload.currentPage;
  }
};

/** 搜索条件变化时回到第一页 */
watch(keyword, () => {
  currentPage.value = 1;
});

const enabledSceneCount = computed(
  () => scenes.value.filter(item => item.enabled).length
);

const summaryText = computed(() => {
  return `已开通 ${enabledSceneCount.value} 个场景，工业价均已默认带入平台价`;
});

const getPlatformRange = (row: EditableScene): [number, number] => [
  row.platformPriceLower,
  row.platformPriceUpper
];

const validateRow = (row: EditableScene) => {
  const platform = getPlatformRange(row);
  const err = validatePriceRange(row.minText, row.maxText, platform, true);
  row.minError = err.min;
  row.maxError = err.max;

  // 硬性错误优先；无硬性错误时展示超出平台区间提示
  if (hasRangeError(err)) {
    row.minWarn = '';
    row.maxWarn = '';
  } else {
    const warn = getPlatformRangeWarning(row.minText, row.maxText, platform);
    row.minWarn = warn.min;
    row.maxWarn = warn.max;
  }

  return !hasRangeError(err);
};

/** 将工业通用价恢复为平台价格区间 */
const onRestorePlatformPrice = (row: EditableScene) => {
  row.minText = formatPriceInputText(row.platformPriceLower);
  row.maxText = formatPriceInputText(row.platformPriceUpper);
  row.industryPriceLower = null;
  row.industryPriceUpper = null;
  row.minError = '';
  row.maxError = '';
  row.minWarn = '';
  row.maxWarn = '';
  XrkMessage.success('已恢复为平台价，请点击保存按钮后生效');
};

const columns: BaseTableProps['columns'] = [
  useTableColumn({
    type: 'index',
    label: '序号',
    width: 80
  }),
  useTableColumn({
    label: '场景路径',
    prop: 'scenePath',
    minWidth: 260
  }),
  useTableColumn({
    label: '平台价格区间',
    prop: 'platformPrice',
    width: 160,
    renderHeader: () => (
      <>
        <div style="text-align: center;">
          <div>项目结算单价</div>
          <div>（平台价格区间）</div>
        </div>
      </>
    ),
    render: ({ row }) => (
      <span>
        {formatPriceRange(row.platformPriceLower, row.platformPriceUpper)}
      </span>
    )
  }),
  useTableColumn({
    label: '工业通用价',
    prop: 'industryPrice',
    width: 220,
    renderHeader: () => (
      <>
        <div style="text-align: center;">
          <div>项目结算单价</div>
          <div>（工业通用价）</div>
        </div>
      </>
    ),
    render: ({ row }) => (
      <div class="price-range-input">
        <div
          style="display:flex;align-items:center;"
          class="price-range-input__row"
        >
          <BaseInput
            class="price-range-input__field"
            style={{ width: '72px' }}
            type="number"
            min={MIN_PRICE}
            max={MAX_PRICE}
            decimals={PRICE_DECIMALS}
            placeholder="下限"
            v-model={row.minText}
            onInput={() => validateRow(row)}
          />
          <span class="price-range-input__sep">&nbsp;~&nbsp;</span>
          <BaseInput
            class="price-range-input__field"
            style={{ width: '72px' }}
            type="number"
            min={MIN_PRICE}
            max={MAX_PRICE}
            decimals={PRICE_DECIMALS}
            placeholder="上限"
            v-model={row.maxText}
            onInput={() => validateRow(row)}
          />
          <span class="price-range-input__unit">元</span>
        </div>
        {row.minError || row.maxError ? (
          <div class="field-error">{row.minError || row.maxError}</div>
        ) : row.minWarn || row.maxWarn ? (
          <div class="field-warn">{row.minWarn || row.maxWarn}</div>
        ) : null}
      </div>
    )
  }),
  useTableColumn({
    label: '操作',
    prop: 'handle',
    fixed: 'right',
    width: 120,
    render: ({ row }) => (
      <BaseButton
        link
        type="primary2"
        onClick={() => onRestorePlatformPrice(row)}
      >
        恢复平台价
      </BaseButton>
    )
  })
];

const normalizeScenes = (list: ScenePriceSceneItem[]) =>
  list.map(item => {
    const platform = getPlatformRange(item as EditableScene);
    const effective = getEffectiveIndustryRange(
      platform,
      item.industryPriceLower,
      item.industryPriceUpper
    );
    return reactive<EditableScene>({
      ...item,
      enabled: item.enabled ?? true,
      minText: formatPriceInputText(effective[0]),
      maxText: formatPriceInputText(effective[1]),
      minError: '',
      maxError: '',
      minWarn: '',
      maxWarn: ''
    });
  });

const fetchDetail = async () => {
  loading.value = true;
  try {
    const res = await getConfigDetail({
      industryId: industryId.value,
      industryName: industryName.value
    });
    const data: ScenePriceConfigDetail | undefined = res.data;
    industryId.value = String(data?.industryId ?? industryId.value);
    industryName.value = data?.industryName || industryName.value;
    scenes.value = normalizeScenes(data?.scenes ?? []);
    currentPage.value = 1;
    if (isCreateMode.value) {
      scenes.value.forEach(item => {
        item.enabled = false;
        item.industryPriceLower = null;
        item.industryPriceUpper = null;
        item.minText = formatPriceInputText(item.platformPriceLower);
        item.maxText = formatPriceInputText(item.platformPriceUpper);
      });
    }
  } catch (error) {
    console.error('获取场景价格配置失败:', error);
  } finally {
    loading.value = false;
  }
};

const onBack = async () => {
  try {
    await XrkMessageBox.confirm(
      '是否确认返回列表，返回后当前页面内容将不会保存',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消'
      }
    );
    router.push('/scene-price-mgmt/list');
  } catch {
    // 用户取消
  }
};

const onSave = async () => {
  if (!industryId.value) {
    XrkMessage.warning('缺少工业信息，无法保存');
    return;
  }

  const enabledScenes = scenes.value.filter(item => item.enabled);

  // 硬性校验：区间高低、必填等有问题则不能保存
  for (const row of enabledScenes) {
    if (!validateRow(row)) {
      XrkMessage.warning('请检查工业通用价区间（须由低到高）');
      return;
    }
  }

  const hasOutOfPlatform = enabledScenes.some(row =>
    isOutOfPlatformRange(row.minText, row.maxText, getPlatformRange(row))
  );

  // 超出平台区间时二次确认
  if (hasOutOfPlatform) {
    try {
      await XrkMessageBox.confirm(
        '您设置的价格已超出平台市场指导价格区间。平台市场指导价格仅供参考，不影响您自主定价。请确认是否继续按当前价格执行。',
        '提示',
        {
          confirmButtonText: '确认',
          cancelButtonText: '取消'
        }
      );
    } catch {
      return;
    }
  }

  saving.value = true;
  try {
    const nextScenes = scenes.value.map(item => {
      const isPlatformPrice =
        item.enabled &&
        Number(item.minText) === item.platformPriceLower &&
        Number(item.maxText) === item.platformPriceUpper;

      return {
        ...item,
        industryPriceLower: item.enabled
          ? isPlatformPrice
            ? null
            : Number(item.minText)
          : null,
        industryPriceUpper: item.enabled
          ? isPlatformPrice
            ? null
            : Number(item.maxText)
          : null
      };
    });

    await saveScenes(industryId.value, nextScenes);
    scenes.value = normalizeScenes(nextScenes);
    XrkMessage.success('已保存工业场景价格');
    router.push('/scene-price-mgmt/list');
  } catch (error) {
    console.error('保存场景价格配置失败:', error);
  } finally {
    saving.value = false;
  }
};

const onOpenPicker = () => {
  useCreateModal(ScenePriceScenePickerModal, {
    industryId: industryId.value,
    scenes: scenes.value,
    onConfirm(nextScenes: ScenePriceSceneItem[]) {
      scenes.value = normalizeScenes(nextScenes);
      currentPage.value = 1;
    }
  });
};

onMounted(() => {
  fetchDetail();
});
</script>

<style lang="scss" scoped>
.scene-price-config {
  height: 100%;
  padding: 20px;
  box-sizing: border-box;
  overflow: hidden;
}
.page-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  background: #fff;
  border-radius: 4px;
  padding: 20px;
  margin-bottom: 16px;
  flex-shrink: 0;
}
.page-header__title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.divider {
  color: #d1d5db;
}
.page-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}
.industry-name {
  font-size: 14px;
  color: #4b5563;
}
.industry-name__value {
  font-weight: 500;
  color: #1f2937;
}
.page-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #9ca3af;
  line-height: 1.6;
}
.page-header__actions {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
}
.toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  flex-shrink: 0;
}
.toolbar-summary {
  font-size: 12px;
  color: #9ca3af;
}
.table-wrap {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 4px;
  padding: 16px;
  overflow: hidden;
}
.table-wrap__body {
  flex: 1;
  min-height: 0;
  overflow: auto;
}
.config-pagination {
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
  margin-top: 16px;
}
.price-range-input {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.price-range-input__row {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 6px;
}
.price-range-input__field {
  flex-shrink: 0;
}
.price-range-input__sep,
.price-range-input__unit {
  flex-shrink: 0;
  color: #9ca3af;
  line-height: 1;
}
.field-error {
  margin-top: 4px;
  font-size: 12px;
  color: #ef4444;
  line-height: 16px;
}
.field-warn {
  margin-top: 4px;
  font-size: 12px;
  color: #f59e0b;
  line-height: 16px;
}
</style>
